import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  Form,
  Button,
  InputGroup,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import { db } from "../firebase/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
const logo = require("../assets/sphera.png");
const user_iwd = require("../assets/img/user_iwd.png");
import "../css/userPage.css";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { formatFirestoreDate, formatISODate } from "./Dashboard";
const remove_icon = require("../assets/remove.png");

const cardBodyStyle = {
  display: "flex",
  alignItems: "center",
  border: "1px solid #CE9136",
  borderRadius: "10px",
};

const fetchUserLastTests = async (usersData) => {
  try {
    const userTestsPromises = usersData.map(async (user) => {
      const userTestsQuery = query(
        collection(db, "UserTests"),
        where("userId", "==", user.id),
        orderBy("testDate", "desc"),
        limit(1)
      );
      const userTestsSnapshot = await getDocs(userTestsQuery);
      if (!userTestsSnapshot.empty) {
        return {
          userId: user.id,
          lastTestDate: userTestsSnapshot.docs[0].data().testDate,
        };
      }
      return null;
    });

    const userTests = await Promise.all(userTestsPromises);
    const lastTestsMap = {};
    userTests.forEach((test) => {
      if (test) {
        lastTestsMap[test.userId] = test.lastTestDate;
      }
    });
    return lastTestsMap;
  } catch (error) {
    console.error("Erreur lors de la récupération des derniers tests:", error);
    return {};
  }
};

const User = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const history = useHistory();
  const [sortOrder, setSortOrder] = useState("asc"); // Changé à 'asc' par défaut
  const [userLastTests, setUserLastTests] = useState({});
  const [sortField, setSortField] = useState("name"); // Ajout de l'état pour le champ de tri
  const role = localStorage.getItem("role");
  const [currentUserCompanyId, setCurrentUserCompanyId] = useState(null);

  // Nouveaux états pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Limite d'éléments par page

  // Calculer les utilisateurs paginés
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  
  // Calculer le nombre total de pages
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        let usersData = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((user) => user.role !== "referent"); // Filtrer les utilisateurs qui ne sont pas référents

        // Filtrer les utilisateurs si le rôle est "referent"
        if (role === "referent") {
          const currentUserId = localStorage.getItem("userIdStorage");
          usersData = usersData.filter(
            (user) => user.referentId === currentUserId
          );
          
          // Récupérer l'entreprise du référent connecté
          const currentUserDoc = querySnapshot.docs.find(
            (doc) => doc.id === currentUserId
          );
          if (currentUserDoc) {
            const currentUserData = currentUserDoc.data();
            setCurrentUserCompanyId(currentUserData.companyId || currentUserData.company);
          }
        }

        // Récupérer les derniers tests avant le tri
        const lastTestsMap = await fetchUserLastTests(usersData);
        setUserLastTests(lastTestsMap);

        // Tri selon le champ sélectionné
        const sortedUsers = usersData.sort((a, b) => {
          if (sortField === "name") {
            const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
            const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
            return sortOrder === "asc"
              ? nameA.localeCompare(nameB)
              : nameB.localeCompare(nameA);
          } else if (sortField === "lastTest") {
            const dateA = lastTestsMap[a.id]
              ? new Date(lastTestsMap[a.id])
              : new Date(0);
            const dateB = lastTestsMap[b.id]
              ? new Date(lastTestsMap[b.id])
              : new Date(0);
            return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
          }
          return 0;
        });

        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des utilisateurs : ",
          error
        );
      }
    };

    const fetchCompanies = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "companies"));
        const companiesData = {};
        querySnapshot.docs.forEach((doc) => {
          companiesData[doc.id] = doc.data().companyName;
        });
        setCompanies(companiesData);
      } catch (error) {
        console.error("Error fetching companies: ", error);
      }
    };

    fetchUsers();
    fetchCompanies();
  }, [sortOrder, sortField, role]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const queryText = searchQuery; // searchQuery.toLowerCase();

    try {
      // Requêtes de recherche insensibles à la casse
      const lastNameQuery = query(
        collection(db, "users"),
        where("lastName", ">=", queryText),
        where("lastName", "<=", queryText + "\uf8ff")
      );
      const firstNameQuery = query(
        collection(db, "users"),
        where("firstName", ">=", queryText),
        where("firstName", "<=", queryText + "\uf8ff")
      );
      const emailQuery = query(
        collection(db, "users"),
        where("email", ">=", queryText),
        where("email", "<=", queryText + "\uf8ff")
      );

      const [lastNameSnapshot, firstNameSnapshot, emailSnapshot] =
        await Promise.all([
          getDocs(lastNameQuery),
          getDocs(firstNameQuery),
          getDocs(emailQuery),
        ]);

      const lastNameResults = lastNameSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const firstNameResults = firstNameSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const emailResults = emailSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const combinedResults = [
        ...lastNameResults,
        ...firstNameResults,
        ...emailResults,
      ].reduce((acc, user) => {
        if (!acc.find((u) => u.id === user.id)) {
          acc.push(user);
        }
        return acc;
      }, []);

      setFilteredUsers(combinedResults);
    } catch (error) {
      console.error("Error searching users: ", error);
    }
  };

  const handleCompanyClick = (companyId) => {
    if (companyId) {
      history.push(`/admin/entreprise/users?companyId=${companyId}`);
    }
  };

  const handleUserClick = (userId, userRole) => {
    if (userId) {
      history.push(`/admin/results?userId=${userId}`);

      /* if (userRole === "referent") {
        history.push(`/admin/list/referent/users?userId=${userId}`);
      } else {
      } */
    }
  };

  const handleDeleteUser = (userId) => {
    setSelectedUser(userId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedUser) {
      try {
        // Supprimer l'utilisateur de Firestore
        await deleteDoc(doc(db, "users", selectedUser));

        // Mettre à jour l'interface
        setUsers(users.filter((user) => user.id !== selectedUser));
        setFilteredUsers(
          filteredUsers.filter((user) => user.id !== selectedUser)
        );
        setShowDeleteModal(false);
        setSelectedUser(null);
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de l'utilisateur : ",
          error
        );
      }
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleReferentClick = (referentId) => {
    if (referentId) {
      history.push(`/admin/list/referent/users?userId=${referentId}`);
    }
  };

  return (
    <Container>
      <Row className="my-4 align-items-center">
        <Col md={6} style={{ display: "flex", flexDirection: "row", gap: 20 }}>
          <img
            src={logo}
            alt="Insights"
            className="img-fluid"
            style={{ height: 158 }}
          />
          <div>
            <h1 className="title">
              {role === "referent" ? "Clients" : "Utilisateurs"}
            </h1>
            <span className="subtitle">
              Accéder aux résultats des{" "}
              {role === "referent" ? "clients" : "utilisateurs"}
            </span>
          </div>
        </Col>
        <Col
          md={6}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <Form
            inline
            style={{ display: "flex", flex: 1, alignItems: "center" }}
            onSubmit={handleSearch}>
            <Form.Control
              type="text"
              placeholder="Recherche"
              className=""
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <button
              type="submit"
              style={{
                backgroundColor: "rgba(206, 145, 54, 0.36)",
                border: "none",
                borderRadius: "5px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#CE9136",
                height: "40px",
                width: "40px",
                alignSelf: "flex-end",
                marginLeft: 6,
              }}>
              <i
                className="fa fa-search"
                aria-hidden="true"
                style={{ fontSize: 20 }}></i>
            </button>
          </Form>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Row className="mt-4 align-items-center">
            <Col>
              <span className="title-kpi">
                Sélectionnez un{" "}
                <span style={{ color: "#CE9136" }}>
                  {role === "referent" ? "client" : "utilisateur"}
                </span>
              </span>
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              <Button
                variant="link"
                onClick={() => {
                  setSortField("name");
                  toggleSortOrder();
                }}>
                {sortOrder === "asc"
                  ? "Trier par nom A à Z"
                  : "Trier par nom Z à A"}
              </Button>
              <Button
                variant="link"
                className="ml-2"
                onClick={() => {
                  setSortField("lastTest");
                  toggleSortOrder();
                }}>
                {sortOrder === "asc"
                  ? "Trier par dernière sphère ↑"
                  : "Trier par dernière sphère ↓"}
              </Button>
            </Col>
          </Row>
          <Card className="card">
            <Card.Body>
              <Table hover>
                <thead>
                  <tr>
                    <th>Select {role === "referent" ? "client" : "user"}</th>
                    <th>Nom / Prénom</th>
                    <th>Email</th>
                    <th>Date d'inscription</th>
                    <th>Dernière sphère</th>
                    <th>Entreprise</th>
                    <th>Business unit</th>
                    {role !== "referent" && <th>Référent</th>}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr
                      key={user.id}
                      //onClick={() => handleReferentClick(user.referentId)}
                      style={{ cursor: "pointer" }}>
                      <td
                        className="text-center align-middle"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleUserClick(user.id, user.role)}>
                        <span className="img-conent">
                          <img src={user_iwd} alt="user_iwd" className="icon" />
                        </span>
                      </td>
                      <td>{`${user.lastName} ${user.firstName}`}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        {userLastTests[user.id]
                          ? new Date(
                              userLastTests[user.id]
                            ).toLocaleDateString()
                          : "-"}
                      </td>
                      <td
                        className={role === "referent" && user.company !== currentUserCompanyId ? "" : "bold-brown"}
                        style={{ 
                          cursor: role === "referent" && user.company !== currentUserCompanyId ? "default" : "pointer",
                          color: role === "referent" && user.company !== currentUserCompanyId ? "inherit" : undefined
                        }}
                        onClick={() => {
                          // Un référent ne peut cliquer que sur sa propre entreprise
                          if (role === "referent" && user.company !== currentUserCompanyId) {
                            return;
                          }
                          handleCompanyClick(user.company);
                        }}>
                        {companies[user.company]?.toUpperCase() || "Inconnu"}
                      </td>
                      <td>{user.businessUnit}</td>
                      {role !== "referent" && (
                        <td
                        /* className="bold-brown" */
                        /* onClick={(e) => {
                            e.stopPropagation();
                            if (user.referentId) {
                              handleReferentClick(user.referentId);
                            }
                          }} */
                        /*  style={{
                            cursor: user.referentId ? "pointer" : "default",
                          }} */
                        >
                          {(user.referentName || "-")?.toUpperCase()}
                        </td>
                      )}
                      <td>
                        <span
                          onClick={() => handleDeleteUser(user.id)}
                          className="img-content"
                          style={{
                            cursor: "pointer",
                            padding: "5px",
                            borderRadius: "5px",
                          }}>
                          <img
                            src={remove_icon}
                            alt="remove_icon"
                            className="icon"
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "20px",
            }}>
            <Button
              variant="link"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}>
              Précédent
            </Button>
            <span style={{ margin: "0 10px" }}>Page {currentPage}/{totalPages}</span>
            <Button
              variant="link"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}>
              Suivant
            </Button>
          </div>
        </Col>
      </Row>
      <ConfirmDeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={confirmDelete}
        title={"Supprimer définitivement cette utilisateur ?"}
        companyName={""}
      />
    </Container>
  );
};

export default User;
